// =============================================================================
// MeuDriver — Admin Dashboard (orchestrator)
// -----------------------------------------------------------------------------
// Cada bloco (KPIs, chart, pendentes, recentes) virou subcomponente em
// `./dashboard/`. Esta página orquestra os fetches e o export CSV.
// =============================================================================

import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toast } from 'sonner'
import { Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/lib/supabase'
import { formatDate } from '@/lib/utils'
import { useSEO } from '@/hooks/useSEO'
import type { InstructorWithProfile, BookingWithDetails } from '@/types/database'

import { AdminKPICards, type KpiData } from './dashboard/AdminKPICards'
import { AdminMonthlyChart, type MonthTrend } from './dashboard/AdminMonthlyChart'
import { AdminPendingInstructors } from './dashboard/AdminPendingInstructors'
import { AdminRecentBookings } from './dashboard/AdminRecentBookings'

type BookingExportRow = {
  id: string
  scheduled_at: string
  status: string
  total_price: number | null
  duration_minutes: number | null
  platform_fee: number | null
  student: { full_name: string | null } | null
  instructor: { profile: { full_name: string | null } | null } | null
}

export default function AdminDashboard() {
  useSEO({ title: 'Painel Admin', noIndex: true })

  const [kpi, setKpi] = useState<KpiData>({
    totalApproved: 0,
    totalPending: 0,
    lessonsThisMonth: 0,
    revenueThisMonth: 0,
    totalStudents: 0,
  })
  const [kpiLoading, setKpiLoading] = useState(true)

  const [trendData, setTrendData] = useState<MonthTrend[]>([])
  const [trendLoading, setTrendLoading] = useState(true)
  const [exporting, setExporting] = useState(false)

  const [pendingInstructors, setPendingInstructors] = useState<InstructorWithProfile[]>([])
  const [pendingLoading, setPendingLoading] = useState(true)

  const [recentBookings, setRecentBookings] = useState<BookingWithDetails[]>([])
  const [bookingsLoading, setBookingsLoading] = useState(true)

  async function fetchKpi() {
    setKpiLoading(true)
    try {
      const now = new Date()
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString()

      const [approvedRes, pendingRes, lessonsRes, revenueRes, studentsRes] = await Promise.all([
        supabase.from('instructors').select('id', { count: 'exact', head: true }).eq('status', 'approved'),
        supabase.from('instructors').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase
          .from('bookings')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'completed')
          .gte('scheduled_at', monthStart)
          .lte('scheduled_at', monthEnd),
        supabase
          .from('bookings')
          .select('platform_fee')
          .eq('status', 'completed')
          .gte('scheduled_at', monthStart)
          .lte('scheduled_at', monthEnd),
        supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('role', 'student'),
      ])

      const totalRevenue = (revenueRes.data ?? []).reduce((sum, b) => sum + (b.platform_fee ?? 0), 0)

      setKpi({
        totalApproved: approvedRes.count ?? 0,
        totalPending: pendingRes.count ?? 0,
        lessonsThisMonth: lessonsRes.count ?? 0,
        revenueThisMonth: totalRevenue,
        totalStudents: studentsRes.count ?? 0,
      })
    } catch {
      toast.error('Erro ao carregar KPIs')
    } finally {
      setKpiLoading(false)
    }
  }

  async function fetchTrend() {
    setTrendLoading(true)
    try {
      const now = new Date()
      const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1)

      const { data } = await supabase
        .from('bookings')
        .select('scheduled_at, total_price, status')
        .gte('scheduled_at', sixMonthsAgo.toISOString())

      const months: Record<string, MonthTrend> = {}
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        months[key] = { month: format(d, 'MMM/yy', { locale: ptBR }), bookings: 0, revenue: 0 }
      }

      for (const b of data ?? []) {
        const d = new Date(b.scheduled_at)
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        if (months[key] && b.status === 'completed') {
          months[key].bookings++
          months[key].revenue += b.total_price ?? 0
        }
      }

      setTrendData(Object.values(months))
    } catch {
      // non-critical — silently ignore
    } finally {
      setTrendLoading(false)
    }
  }

  async function handleExport() {
    setExporting(true)
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(
          'id, scheduled_at, status, total_price, duration_minutes, platform_fee, student:profiles!bookings_student_id_fkey(full_name), instructor:instructors!bookings_instructor_id_fkey(profile:profiles(full_name))',
        )
        .order('scheduled_at', { ascending: false })

      if (error) throw error

      const rows = [
        ['ID', 'Data', 'Aluno', 'Instrutor', 'Status', 'Duração (min)', 'Valor', 'Taxa plataforma'],
        ...((data ?? []) as unknown as BookingExportRow[]).map((b) => [
          b.id,
          formatDate(b.scheduled_at, 'dd/MM/yyyy HH:mm'),
          b.student?.full_name ?? '',
          b.instructor?.profile?.full_name ?? '',
          b.status,
          b.duration_minutes ?? '',
          b.total_price ?? '',
          b.platform_fee ?? '',
        ]),
      ]

      const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `meudriver-aulas-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Relatório exportado!')
    } catch {
      toast.error('Erro ao exportar relatório.')
    } finally {
      setExporting(false)
    }
  }

  async function fetchPendingInstructors() {
    setPendingLoading(true)
    try {
      const { data, error } = await supabase
        .from('instructors')
        .select('*, profile:profiles(*)')
        .eq('status', 'pending')
        .order('created_at', { ascending: true })
        .limit(20)
      if (error) throw error
      setPendingInstructors((data as unknown as InstructorWithProfile[]) ?? [])
    } catch {
      toast.error('Erro ao carregar instrutores pendentes')
    } finally {
      setPendingLoading(false)
    }
  }

  async function fetchRecentBookings() {
    setBookingsLoading(true)
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(
          '*, student:profiles!bookings_student_id_fkey(*), instructor:instructors!bookings_instructor_id_fkey(*, profile:profiles(*)), payment:payments(*)',
        )
        .order('created_at', { ascending: false })
        .limit(10)
      if (error) throw error
      setRecentBookings((data as unknown as BookingWithDetails[]) ?? [])
    } catch {
      toast.error('Erro ao carregar aulas recentes')
    } finally {
      setBookingsLoading(false)
    }
  }

  useEffect(() => {
    fetchKpi()
    fetchTrend()
    fetchPendingInstructors()
    fetchRecentBookings()
  }, [])

  return (
    <div className="space-y-8 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Painel Administrativo</h1>
          <p className="text-muted-foreground mt-1">Visão geral da plataforma MeuDriver</p>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={exporting} className="gap-2 self-start sm:self-auto">
          <Download className="h-4 w-4" />
          {exporting ? 'Exportando...' : 'Exportar relatório'}
        </Button>
      </div>

      <AdminKPICards kpi={kpi} loading={kpiLoading} />

      <AdminMonthlyChart data={trendData} loading={trendLoading} />

      <AdminPendingInstructors
        pending={pendingInstructors}
        loading={pendingLoading}
        totalPending={kpi.totalPending}
        onChanged={async () => {
          await Promise.all([fetchPendingInstructors(), fetchKpi()])
        }}
      />

      <AdminRecentBookings bookings={recentBookings} loading={bookingsLoading} />
    </div>
  )
}
